import {
  useState,
} from 'react';
import {
  Callout,
  Confirm,
  Field,
  FileInput,
  Modal,
  ScreenWithSections,
  SearchRow,
  TextInput,
  Chip,
  Empty,
  Loading,
  Panel,
  TableScroll,
  explainFailure,
  useLoad,
  usePaging,
  Pager,
} from '@affdice/portal-kit';
import {
  PlatformApi,
} from '../../client.ts';
import {
  importFailuresOf,
  refEditPayload,
  refRowsMatching,
  type RefImportFailure,
} from '../../screens.ts';
import { Failure, EditIcon, DeleteIcon, IconButton } from '../shared.tsx';
import { t, tCount, type AmsMessageKey } from '../../messages.ts';

/**
 * Item 7 — products, jurisdictions, currencies, devices and OS, each with
 * its own editable fields but the same shape otherwise (a code, an active
 * flag, add/edit/deactivate/delete) — one generic table + form rather than
 * five near-identical screens.
 */
interface RefFieldDef {
  readonly key: string;
  readonly label: AmsMessageKey;
  readonly kind: 'text' | 'number' | 'boolean';
  /**
   * Editable on Add, read-only on Edit — the field still shows the row's
   * current value once a row exists, but the Save button never offers it
   * back. A jurisdiction's `country`/`subdivision`: 0002's CHECK ties `code`
   * to both, and the API now refuses an Edit that changes either.
   */
  readonly readOnlyOnEdit?: boolean;
}

interface RefTypeDef {
  readonly basePath: string;
  readonly label: AmsMessageKey;
  /** The singular of `label`, for "Add {item}". */
  readonly singular: AmsMessageKey;
  readonly codeLabel: AmsMessageKey;
  readonly fields: readonly RefFieldDef[];
  /**
   * Migration 0172 — a catalogue that is curated, not created.
   *
   * The commission engine calculates on these codes by name, so the five
   * commission-field catalogues have no Add and no Delete: a sixth revenue
   * basis would be offered on an operator's form and then price nothing. What
   * is on offer here is renaming one, reordering them, and switching off the
   * ones this platform does not use.
   *
   * The routes for adding and deleting are not registered for these five and
   * the application role holds no INSERT on the tables, so this hides two
   * buttons whose only possible outcome is a refusal — it is not what stops
   * the write.
   */
  readonly curated?: boolean;
}

const REF_TYPES: readonly RefTypeDef[] = [
  {
    basePath: '/v1/platform/ref/products', label: 'plat.ref.type.products', singular: 'plat.ref.single.products',
    codeLabel: 'plat.ref.code',
    fields: [
      { key: 'name', label: 'common.name', kind: 'text' },
      { key: 'is_fee_based', label: 'plat.ref.field.feeBased', kind: 'boolean' },
    ],
  },
  {
    basePath: '/v1/platform/ref/jurisdictions', label: 'plat.ref.type.jurisdictions', singular: 'plat.ref.single.jurisdictions',
    codeLabel: 'plat.ref.codeJurisdiction',
    fields: [
      // Set at creation and never after — 0002's CHECK ties `code` to both,
      // and the API refuses an Edit that changes either.
      { key: 'country', label: 'plat.ref.field.country', kind: 'text', readOnlyOnEdit: true },
      { key: 'subdivision', label: 'plat.ref.field.subdivision', kind: 'text', readOnlyOnEdit: true },
      { key: 'name', label: 'common.name', kind: 'text' },
      { key: 'regulates_by_subdivision', label: 'plat.ref.field.regulatesBySubdivision', kind: 'boolean' },
    ],
  },
  {
    basePath: '/v1/platform/ref/currencies', label: 'plat.ref.type.currencies', singular: 'plat.ref.single.currencies',
    codeLabel: 'plat.ref.codeCurrency',
    fields: [
      { key: 'name', label: 'common.name', kind: 'text' },
      { key: 'minor_unit_scale', label: 'plat.ref.field.minorUnitScale', kind: 'number' },
      { key: 'is_crypto', label: 'plat.ref.field.crypto', kind: 'boolean' },
    ],
  },
  {
    basePath: '/v1/platform/ref/devices', label: 'plat.ref.type.devices', singular: 'plat.ref.single.devices',
    codeLabel: 'plat.ref.code',
    fields: [{ key: 'label', label: 'plat.ref.field.label', kind: 'text' }],
  },
  {
    basePath: '/v1/platform/ref/os', label: 'plat.ref.type.os', singular: 'plat.ref.single.os',
    codeLabel: 'plat.ref.code',
    fields: [
      { key: 'label', label: 'plat.ref.field.label', kind: 'text' },
      // Item 17 (third batch) — what makes a new OS actually classify. Without
      // a pattern the row exists in the catalogue and no click ever matches it.
      { key: 'detect_pattern', label: 'plat.ref.field.detectPattern', kind: 'text' },
      { key: 'sort_order', label: 'plat.ref.field.matchOrder', kind: 'number' },
    ],
  },

  /*
   * Migration 0172 — what the operator console's campaign wizard and
   * deal-proposal form offer in each of their commission fields.
   *
   * `sort_order` earns its place on all five: it is the order the picker
   * shows, and the top active row is what new terms are written on for an
   * operator who has that field switched off. Moving a row to the top is
   * therefore how this platform's default is set, which is a thing worth
   * being able to do without a second setting that means the same.
   */
  {
    basePath: '/v1/platform/ref/commission-types', label: 'plat.ref.type.commissionTypes', singular: 'plat.ref.single.commissionTypes',
    codeLabel: 'plat.ref.code', curated: true,
    fields: [
      { key: 'label', label: 'plat.ref.field.label', kind: 'text' },
      { key: 'sort_order', label: 'plat.ref.field.orderDefault', kind: 'number' },
    ],
  },
  {
    basePath: '/v1/platform/ref/revenue-bases', label: 'plat.ref.type.revenueBases', singular: 'plat.ref.single.revenueBases',
    codeLabel: 'plat.ref.code', curated: true,
    fields: [
      { key: 'label', label: 'plat.ref.field.label', kind: 'text' },
      { key: 'sort_order', label: 'plat.ref.field.orderDefault', kind: 'number' },
    ],
  },
  {
    basePath: '/v1/platform/ref/tier-metrics', label: 'plat.ref.type.tierMetrics', singular: 'plat.ref.single.tierMetrics',
    codeLabel: 'plat.ref.code', curated: true,
    fields: [
      { key: 'label', label: 'plat.ref.field.label', kind: 'text' },
      { key: 'sort_order', label: 'plat.ref.field.orderDefault', kind: 'number' },
    ],
  },
  {
    basePath: '/v1/platform/ref/hybrid-modes', label: 'plat.ref.type.hybridModes', singular: 'plat.ref.single.hybridModes',
    codeLabel: 'plat.ref.code', curated: true,
    fields: [
      { key: 'label', label: 'plat.ref.field.label', kind: 'text' },
      { key: 'sort_order', label: 'plat.ref.field.orderDefault', kind: 'number' },
    ],
  },
  {
    basePath: '/v1/platform/ref/cpa-triggers', label: 'plat.ref.type.cpaTriggers', singular: 'plat.ref.single.cpaTriggers',
    codeLabel: 'plat.ref.code', curated: true,
    fields: [
      { key: 'label', label: 'plat.ref.field.label', kind: 'text' },
      { key: 'sort_order', label: 'plat.ref.field.orderDefault', kind: 'number' },
    ],
  },
  /*
   * Migration 0173 — the two fields on the same forms whose tables predate the
   * pattern. Neither has a "default": a deduction stack and a criteria list
   * are built from the catalogue rather than picked from it, so `sort_order`
   * here is only the order they are offered in — which is still worth setting,
   * because it is the order somebody reads eleven checkboxes in.
   */
  {
    basePath: '/v1/platform/ref/deduction-items', label: 'plat.ref.type.deductionItems', singular: 'plat.ref.single.deductionItems',
    codeLabel: 'plat.ref.code', curated: true,
    fields: [
      { key: 'label', label: 'plat.ref.field.label', kind: 'text' },
      { key: 'sort_order', label: 'plat.ref.field.order', kind: 'number' },
    ],
  },
  {
    basePath: '/v1/platform/ref/cpa-criteria', label: 'plat.ref.type.cpaCriteria', singular: 'plat.ref.single.cpaCriteria',
    codeLabel: 'plat.ref.code', curated: true,
    fields: [
      { key: 'label', label: 'plat.ref.field.label', kind: 'text' },
      { key: 'sort_order', label: 'plat.ref.field.order', kind: 'number' },
    ],
  },
];

/**
 * Item 10 (third batch) — the same left-rail split Markets and the operator
 * popup already use, instead of a bare row of buttons that read as a second,
 * unrelated navigation. `ScreenWithSections` carries the page title too, so
 * the heading and the rail cannot drift apart.
 */
export function ReferenceData({ api, canWrite }: { api: PlatformApi; canWrite: boolean }) {
  const [basePath, setBasePath] = useState<string>(REF_TYPES[0]!.basePath);
  const type = REF_TYPES.find((d) => d.basePath === basePath) ?? REF_TYPES[0]!;

  return (
    <ScreenWithSections
      title={t('nav.referenceData')}
      ariaLabel={t('plat.ref.sectionsLabel')}
      section={basePath}
      onSection={setBasePath}
      groups={[
        {
          label: t('plat.ref.groupCatalogue'),
          items: REF_TYPES.filter((d) => !d.curated).map((d) => ({ id: d.basePath, label: t(d.label) })),
        },
        {
          // Separated because what can be done to them is different, not
          // because of what they are about. Everything above can be added to;
          // nothing below can — see RefTypeDef.curated.
          label: t('plat.ref.groupCommission'),
          items: REF_TYPES.filter((d) => d.curated).map((d) => ({ id: d.basePath, label: t(d.label) })),
        },
      ]}
    >
      <RefDataPanel key={type.basePath} api={api} type={type} canWrite={canWrite} />
    </ScreenWithSections>
  );
}

/**
 * What deactivating a commission-field value actually does, said before it is
 * done rather than discovered by an affiliate whose statement stopped.
 *
 * Three facts in the order that decides the answer: how many live deals are
 * paying under this value today, when they stop, and what does not happen.
 * The count is the one that turns this from a preference into a decision —
 * "this stops 34 live deals next month" is not the same screen as "this stops
 * none", and until the number was on it both looked identical.
 */
function curatedDeactivateWarning(code: string, rows: readonly Record<string, unknown>[]): string {
  const live = Number(rows.find((r) => String(r['code']) === code)?.['live_deals'] ?? 0);

  return live === 0
    ? t('plat.ref.curatedWarnNone', { code })
    : tCount('plat.ref.curatedWarn', live, { code });
}

function emptyFieldValues(fields: readonly RefFieldDef[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) out[f.key] = f.kind === 'boolean' ? false : '';
  return out;
}

function RefFieldInput({
  field,
  value,
  onChange,
}: {
  field: RefFieldDef;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  if (field.kind === 'boolean') {
    return (
      <label className="row" style={{ alignItems: 'center', gap: '0.5rem' }}>
        <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
        {t(field.label)}
      </label>
    );
  }
  return (
    <Field label={t(field.label)}>
      <TextInput
        value={value == null ? '' : String(value)}
        onChange={(v) => onChange(field.kind === 'number' ? (v === '' ? '' : Number(v)) : v)}
      />
    </Field>
  );
}

/**
 * The Active chip, as the control that changes it.
 *
 * A real `<button>`: a `<span>` with an onClick is not a control, it is a
 * control to a mouse and nothing at all to a keyboard.
 *
 * It says which state the row is in first and what pressing it does second —
 * the same order the operator console's language note was corrected to. The
 * state is the fact; the action is what follows from it, and a control
 * carrying only its action leaves the reader working out which way round they
 * are. The visible word is the state alone, and the sentence is the button's
 * accessible name and its tooltip.
 *
 * Nothing here is optimistic. While the write is in flight the chip goes on
 * showing the state the row still has, and says the change has not landed; a
 * refused write leaves it exactly where it was with the reason in the callout
 * above. A chip that flipped on click and flipped back on failure would state
 * something untrue for as long as the request took, which is the one thing a
 * status chip may never do.
 */
function ActiveToggle({
  code,
  active,
  busy,
  onToggle,
}: {
  code: string;
  active: boolean;
  busy: boolean;
  onToggle: () => void;
}) {
  const label = busy
    ? active
      ? t('plat.ref.toggle.busyActive', { code })
      : t('plat.ref.toggle.busyInactive', { code })
    : active
      ? t('plat.ref.toggle.active', { code })
      : t('plat.ref.toggle.inactive', { code });

  return (
    <button
      type="button"
      // Batch 2 item 6 — inactive reference data is off, not provisional.
      className={`chip ${active ? 'locked' : 'neutral'} ref-active-toggle${busy ? ' is-busy' : ''}`}
      aria-label={label}
      title={label}
      {...(busy ? { 'aria-busy': true } : {})}
      disabled={busy}
      onClick={onToggle}
    >
      {active ? t('common.active') : t('common.inactive')}
    </button>
  );
}

/**
 * Import CSV is hidden, not removed: the route, the per-line refusal table and
 * the export it round-trips with all still work, and turning this back on is
 * the whole of bringing it back.
 */
const CSV_IMPORT_VISIBLE = false;

function RefDataPanel({ api, type, canWrite }: { api: PlatformApi; type: RefTypeDef; canWrite: boolean }) {
  const [version, setVersion] = useState(0);
  const [adding, setAdding] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newValues, setNewValues] = useState<Record<string, unknown>>(() => emptyFieldValues(type.fields));
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  // Item 14 (third batch) — deactivating a jurisdiction fans out further
  // than any other reference-data row: every campaign targeting it, across
  // every operator licensed there, pauses along with it. Item 13 (third
  // batch) gives every other type's own deactivate, and delete, the same
  // confirm-first treatment, each with copy scaled to its own consequence.
  const [confirmingDeactivate, setConfirmingDeactivate] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  // Q6 — bulk edit. A catalogue is edited thirty rows at a time or not at all,
  // and the add form above is a form for one.
  const [importing, setImporting] = useState(false);
  const [importText, setImportText] = useState('');
  const [importName, setImportName] = useState('');
  const [importFailures, setImportFailures] = useState<readonly RefImportFailure[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  // A catalogue is a few dozen rows and all of them are already here — the
  // list route fetches the whole table, inactive rows included, and there is
  // no paging. So the box filters what is loaded rather than asking the API
  // again; see `refRowsMatching`. `searchInput` is what is typed and `query`
  // is what has been applied, which is the same two-state SearchRow every
  // other search in this console uses, so pressing Enter or Search means the
  // same thing here as it does on Operators and Affiliates.
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const loaded = useLoad(() => api.refDataList(type.basePath), [type.basePath, version]);

  // The columns the table is actually showing, so a search is checked against
  // what the reader can see. `code` is always searched; `refRowsMatching`
  // adds it.
  const searchableKeys = type.fields.filter((f) => f.kind === 'text').map((f) => f.key);
  const allRows = loaded.state === 'ready' ? loaded.value.items : [];
  const REF_PAGE = 50;
  const matches = refRowsMatching(allRows, query, searchableKeys);
  /*
   * Jurisdictions is 301 rows, and rendering all of them made a panel 13,667px
   * tall. The search above narrows it for anyone who knows what they are
   * looking for; this is for everyone else, who was scrolling thirteen metres.
   *
   * Paged in the client: the whole catalogue arrives in one request and is
   * searched here, so the cost was the DOM rather than the fetch.
   */
  const paging = usePaging(REF_PAGE);
  const shown = matches.slice(0, paging.limit);

  const refresh = () => setVersion((v) => v + 1);
  const fail = (e: unknown) => setFailure(e);

  // Item 15 (third batch) — a jurisdiction's code is not an independent
  // fact: `US-NJ` is its country and subdivision joined, and a country row
  // is just the country. Asking for the code separately let it disagree with
  // the two fields it is made of.
  const derivesCode = type.basePath === '/v1/platform/ref/jurisdictions';
  const derivedCode = derivesCode
    ? [String(newValues['country'] ?? '').trim().toUpperCase(),
       String(newValues['subdivision'] ?? '').trim().toUpperCase()]
        .filter(Boolean)
        .join('-')
    : '';
  const effectiveCode = derivesCode ? derivedCode : newCode;

  const setActive = (code: string, active: boolean) => {
    setBusy(code);
    setFailure(null);
    return api
      .refDataSetActive(type.basePath, code, active)
      .then(refresh)
      .catch(fail)
      .finally(() => setBusy(null));
  };

  return (
    <>
      {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}
      {/* Batch 2 review, finding 7 — the CSV import's own result (what was
          applied); read whole. */}
      {notice ? <Callout tone="note" fold={false}>{notice}</Callout> : null}

      <SearchRow
        value={searchInput}
        onChange={setSearchInput}
        onSearch={() => setQuery(searchInput.trim())}
        placeholder={t('plat.ref.search')}
      />

      <Panel
        title={t(type.label)}
        aside={
          <span className="page-head-actions">
            {canWrite && CSV_IMPORT_VISIBLE ? (
              <button
                className="link-button"
                onClick={() => {
                  setImporting(true);
                  setImportText('');
                  setImportName('');
                  setImportFailures([]);
                  setFailure(null);
                  setNotice(null);
                }}
              >
                {t('plat.ref.importCsv')}
              </button>
            ) : null}
            {canWrite && !type.curated ? (
              <button className="primary" onClick={() => { setAdding(true); setFailure(null); }}>
                {t('plat.ref.add', { item: t(type.singular) })}
              </button>
            ) : null}
          </span>
        }
      >

        {loaded.state === 'loading' ? <Loading rows={2} /> : null}
        {loaded.state === 'error' ? <Failure error={loaded.error} /> : null}
        {loaded.state === 'ready' ? (
          allRows.length === 0 ? (
            <Empty>{t('plat.ref.nothingYet')}</Empty>
          ) : matches.length === 0 ? (
            /*
              Not "Nothing yet." — every row is still there, and one sentence
              for both "this catalogue is empty" and "your search matched
              nothing" is the same words for two very different facts.
            */
            <Empty>
              {t('plat.ref.noMatch', { type: t(type.label).toLowerCase(), query, count: allRows.length })}
            </Empty>
          ) : (
            <TableScroll>
              <table>
                <thead>
                  <tr>
                    <th>{t('plat.ref.code')}</th>
                    {type.fields.map((f) => (
                      <th key={f.key} className={f.kind === 'number' ? 'num' : undefined}>
                        {t(f.label).split(' (')[0]}
                      </th>
                    ))}
                    {/* Migration 0172 — what switching this row off would stop.
                        On the row rather than only in the confirmation, because
                        the question "which of these is safe to turn off?" is
                        asked of the list, not of one row at a time. */}
                    {type.curated ? <th className="num">{t('plat.ref.colLiveDeals')}</th> : null}
                    <th>{t('common.active')}</th><th />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((row) => {
                    const code = String(row['code']);
                    const editing = editingCode === code;
                    return (
                      <tr key={code}>
                        {/* Batch 2 item 12 — a code, like every other one in
                            this console. */}
                        <td className="mono">{code}</td>
                        {type.fields.map((f) =>
                          editing && !f.readOnlyOnEdit ? (
                            <td key={f.key}>
                              <RefFieldInput
                                field={f}
                                value={editValues[f.key]}
                                onChange={(v) => setEditValues((cur) => ({ ...cur, [f.key]: v }))}
                              />
                            </td>
                          ) : (
                            // A field the row was created with (a
                            // jurisdiction's country/subdivision,
                            // `readOnlyOnEdit`) reads the same whether or not
                            // this row is being edited.
                            // A number field is a number wherever it is shown:
                            // the field already declares `kind`, and every one
                            // of these cells rendered flush left because the
                            // cell never asked.
                            <td key={f.key} className={f.kind === 'number' ? 'num faint' : 'faint'}>
                              {f.kind === 'boolean' ? (row[f.key] ? t('common.yes') : t('common.no')) : String(row[f.key] ?? '—')}
                            </td>
                          ),
                        )}
                        {type.curated ? (
                          <td className="num">
                            {Number(row['live_deals'] ?? 0) || '—'}
                          </td>
                        ) : null}
                        <td>
                          {/*
                            The chip is the control now, and the separate
                            Deactivate button is gone: one thing saying what
                            the state is and one thing changing it were two
                            controls for one fact. Deactivating still asks
                            first — Item 13/14 (third batch) put that confirm
                            there for what it fans out to, and moving where
                            the click starts does not make the consequence
                            smaller. Reactivating does not ask, exactly as
                            before.

                            Read-only staff get the chip they always had: a
                            <span>, because for them it genuinely is not a
                            control.
                          */}
                          {canWrite ? (
                            <ActiveToggle
                              code={code}
                              active={Boolean(row['active'])}
                              busy={busy === code}
                              onToggle={() => {
                                if (row['active']) {
                                  setConfirmingDeactivate(code);
                                  return;
                                }
                                void setActive(code, true);
                              }}
                            />
                          ) : row['active'] ? (
                            <Chip tone="locked">{t('common.active')}</Chip>
                          ) : (
                            // Batch 2 item 6 — inactive is off, not provisional.
                            <Chip tone="neutral">{t('common.inactive')}</Chip>
                          )}
                        </td>
                        <td className="num">
                          <span className="ref-row-actions">
                            {editing ? (
                              <button
                                className="copy"
                                disabled={busy === code}
                                onClick={() => {
                                  setBusy(code);
                                  setFailure(null);
                                  api
                                    .refDataUpdate(type.basePath, code, refEditPayload(type.fields, editValues))
                                    .then(() => { setEditingCode(null); refresh(); })
                                    .catch(fail)
                                    .finally(() => setBusy(null));
                                }}
                              >
                                {t('common.save')}
                              </button>
                            ) : canWrite ? (
                              <IconButton
                                label={t('plat.ref.editCode', { code })}
                                disabled={busy === code}
                                onClick={() => {
                                  const init: Record<string, unknown> = {};
                                  for (const f of type.fields) init[f.key] = row[f.key];
                                  setEditValues(init);
                                  setEditingCode(code);
                                  setFailure(null);
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            ) : null}
                            {canWrite && !type.curated ? (
                              <IconButton
                                label={t('plat.ref.deleteCode', { code })}
                                danger
                                disabled={busy === code}
                                onClick={() => setConfirmingDelete(code)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            ) : null}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableScroll>
          )
        ) : null}
        <Pager
          shown={shown.length}
          hasMore={shown.length < matches.length}
          onMore={paging.more}
        />
      </Panel>

      {/*
        Item 15 (third batch) — the Add form was an inline block that pushed
        the table it was about down the page and shared the panel's scroll
        with it. It is a modal now, the same as every other create form in
        this console.
      */}
      {adding && !type.curated ? (
        <Modal title={t('plat.ref.add', { item: t(type.singular) })} onClose={() => setAdding(false)}>
          <div className="form">
            {derivesCode ? (
              /* Item 15 — a jurisdiction's code IS its country and
                 subdivision joined, and asking for all three let them
                 disagree: US + NJ + a hand-typed "US-NY" was accepted. */
              <Field label={t(type.codeLabel)} hint={t('plat.ref.codeHint')}>
                <div className="control faint">{derivedCode || '—'}</div>
              </Field>
            ) : (
              <Field label={t(type.codeLabel)}>
                <TextInput value={newCode} onChange={(v) => setNewCode(v.toUpperCase())} />
              </Field>
            )}
            {type.fields.map((f) => (
              <RefFieldInput
                key={f.key}
                field={f}
                value={newValues[f.key]}
                onChange={(v) => setNewValues((cur) => ({ ...cur, [f.key]: v }))}
              />
            ))}
            <div className="form-actions">
              <button className="copy" onClick={() => setAdding(false)}>{t('common.cancel')}</button>
              <button
                className="primary"
                disabled={!effectiveCode.trim() || busy === 'create'}
                onClick={() => {
                  setBusy('create');
                  setFailure(null);
                  api
                    .refDataCreate(type.basePath, { code: effectiveCode.trim(), ...newValues })
                    .then(() => {
                      setAdding(false);
                      setNewCode('');
                      setNewValues(emptyFieldValues(type.fields));
                      refresh();
                    })
                    .catch(fail)
                    .finally(() => setBusy(null));
                }}
              >
                {t('plat.ref.create')}
              </button>
            </div>
          </div>
        </Modal>
      ) : null}

      {confirmingDeactivate ? (
        <Confirm
          title={t('plat.ref.deactivateTitle', { code: confirmingDeactivate })}
          tone="error"
          confirmLabel={t('plat.ref.deactivate')}
          body={
            type.basePath === '/v1/platform/ref/jurisdictions'
              ? t('plat.ref.deactivateJurisdiction', { code: confirmingDeactivate })
              : type.curated
                ? curatedDeactivateWarning(confirmingDeactivate, allRows)
                : t('plat.ref.deactivateDefault', { code: confirmingDeactivate })
          }
          onCancel={() => setConfirmingDeactivate(null)}
          onConfirm={async () => {
            const code = confirmingDeactivate;
            await setActive(code, false);
            setConfirmingDeactivate(null);
          }}
        />
      ) : null}

      {confirmingDelete ? (
        <Confirm
          title={t('plat.ref.deleteTitle', { code: confirmingDelete })}
          tone="error"
          confirmLabel={t('common.delete')}
          body={t('plat.ref.deleteBody')}
          onCancel={() => setConfirmingDelete(null)}
          onConfirm={async () => {
            const code = confirmingDelete;
            setBusy(code);
            setFailure(null);
            try {
              await api.refDataDelete(type.basePath, code);
              refresh();
            } catch (e) {
              fail(e);
            } finally {
              setBusy(null);
              setConfirmingDelete(null);
            }
          }}
        />
      ) : null}

      {importing ? (
        <Modal title={t('plat.ref.importTitle', { type: t(type.label).toLowerCase() })} onClose={() => setImporting(false)}>
          <div className="form">
            <Callout tone="note">
              {t('plat.ref.importIntro')}
              <strong>{['code', ...type.fields.map((f) => f.key)].join(', ')}</strong>
              {t('plat.ref.importOutro')}
            </Callout>

            <Field label={t('plat.ref.file')}>
              {/*
                The browser's own file control is drawn by the browser, in the
                browser's colours, and on a dark console it reads as a piece
                of some other application that wandered in. `FileInput` is the
                kit's own, and is still a real `<input type="file">` under the
                label, so it keeps the keyboard and the screen reader.
              */}
              <FileInput
                accept=".csv,text/csv"
                fileName={importName}
                onChoose={(file) => {
                  setImportFailures([]);
                  if (!file) { setImportText(''); setImportName(''); return; }
                  setImportName(file.name);
                  void file.text().then(setImportText);
                }}
              />
            </Field>

            {importFailures.length > 0 ? (
              <>
                <Callout tone="error">
                  {t('plat.ref.importNoneApplied', { file: importName || t('plat.ref.theFile') })}
                </Callout>
                <TableScroll>
                  <table>
                    <thead>
                      <tr><th>{t('plat.ref.colLine')}</th><th>{t('plat.ref.code')}</th><th>{t('plat.ref.colProblem')}</th></tr>
                    </thead>
                    <tbody>
                      {importFailures.map((f) => (
                        <tr key={`${f.line}-${f.code}`}>
                          <td className="num">{f.line}</td>
                          <td>{f.code || <span className="faint">—</span>}</td>
                          <td className="faint">{f.problem}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroll>
              </>
            ) : null}

            <div className="form-actions">
              <button className="copy" onClick={() => setImporting(false)}>{t('common.cancel')}</button>
              <button
                className="primary"
                disabled={!importText.trim() || busy === 'import'}
                onClick={() => {
                  setBusy('import');
                  setFailure(null);
                  setImportFailures([]);
                  api
                    .refDataImport(type.basePath, importText)
                    .then((result) => {
                      setImporting(false);
                      setNotice(t('plat.ref.importApplied', { file: importName || t('plat.ref.file'), message: result.message }));
                      refresh();
                    })
                    .catch((e: unknown) => {
                      // A per-row rejection is not a failure to show as one
                      // sentence — the point of validating every row in one
                      // pass is that the person sees every line to fix.
                      const rows = importFailuresOf(e);
                      if (rows.length > 0) setImportFailures(rows);
                      else fail(e);
                    })
                    .finally(() => setBusy(null));
                }}
              >
                {t('plat.ref.import')}
              </button>
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
